import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2, ArrowRight, ArrowLeft, Mail, Lock, User, Gift } from "lucide-react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useMutation } from "@apollo/client/react";
import { useApolloClient } from "@apollo/client/react";
import { SEND_VERIFICATION_CODE, SIGN_UP_WITH_EMAIL, AUTHENTICATE_WITH_GOOGLE } from "@/graphql/mutations";
import { setAuthCookie } from "@/lib/cookie-utils";
import { saveTokens } from "@/lib/token-utils";
import { trackDashboardEvent } from "@/lib/analytics";

// Google Identity Services types
interface GoogleConfig {
  client_id: string;
  callback: (response: { credential: string }) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
}

interface GoogleButtonConfig {
  theme?: string;
  size?: string;
  width?: string;
  text?: string;
}

interface GoogleIdentity {
  accounts: {
    id: {
      initialize: (config: GoogleConfig) => void;
      renderButton: (element: HTMLElement, config: GoogleButtonConfig) => void;
    };
  };
}

declare const google: GoogleIdentity | undefined;

interface SignupMutationTokens {
  accessToken: string;
  refreshToken: string;
}

type SignupStep = "email" | "otp" | "password";

const Signup = () => {
  const [step, setStep] = useState<SignupStep>("email");
  const [email, setEmail] = useState("");
  const [otpCode, setOtpCode] = useState(["", "", "", ""]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const referralCodeRef = useRef("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isGoogleInitialized, setIsGoogleInitialized] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apolloClient = useApolloClient();
  const otpRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];
  const googleButtonRef = useRef<HTMLDivElement>(null);

  const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ||
    "787453514477-33m2jvargdpl27nkg8na1e4dmnq67f82.apps.googleusercontent.com";

  useEffect(() => {
    trackDashboardEvent("auth_view", {
      auth_flow: "signup",
      page_path: "/signup",
      signup_step: step,
    });
  }, [step]);

  useEffect(() => {
    const codeFromURL = searchParams.get("ref");
    if (codeFromURL) {
      setReferralCode(codeFromURL.toUpperCase());
    }
  }, [searchParams]);

  useEffect(() => {
    referralCodeRef.current = referralCode;
  }, [referralCode]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Start cooldown when entering OTP step
  useEffect(() => {
    if (step === "otp") {
      setResendCooldown(30); // 30 second cooldown
    }
  }, [step]);

  // Send verification code mutation
  const [sendVerificationCode, { loading: sendingCode }] = useMutation<{ sendVerificationCode: { success: boolean; message?: string | null; error?: string | null } }>(SEND_VERIFICATION_CODE, {
    onCompleted: (data) => {
      if (data.sendVerificationCode.success) {
        trackDashboardEvent("auth_result", {
          auth_flow: "signup_email_verification",
          result: "success",
        });
        setMessage(data.sendVerificationCode.message || "Verification code sent!");
        setError("");
        setStep("otp");
      } else {
        trackDashboardEvent("auth_result", {
          auth_flow: "signup_email_verification",
          result: "failure",
          error_message: data.sendVerificationCode.error || "verification_failed",
        });
        setError(data.sendVerificationCode.error || "Failed to send verification code");
      }
    },
    onError: (err) => {
      const isNetworkError =
        !!(err as { networkError?: unknown }).networkError ||
        err.message?.includes('Failed to fetch') ||
        err.message?.includes('NetworkError');
      trackDashboardEvent("auth_result", {
        auth_flow: "signup_email_verification",
        result: "failure",
        error_message: isNetworkError ? "network_error" : err.message || "verification_failed",
      });
      setError(
        isNetworkError
          ? "Something went wrong. Please check your connection and try again."
          : err.message || "Failed to send verification code"
      );
    },
  });

  // Sign up mutation
  const [signUpWithEmail, { loading: signingUp }] = useMutation<{ signUpWithEmail: { success: boolean; error?: string | null; tokens?: SignupMutationTokens | null; requiresOnboarding?: boolean | null } }>(SIGN_UP_WITH_EMAIL, {
    onCompleted: async (data) => {
      if (data.signUpWithEmail.success) {
        // Store email in session for onboarding
        sessionStorage.setItem('pendingEmailSignup', email);

        if (data.signUpWithEmail.tokens) {
          saveTokens(data.signUpWithEmail.tokens.accessToken, data.signUpWithEmail.tokens.refreshToken);
          setAuthCookie(data.signUpWithEmail.tokens.accessToken);
          await apolloClient.resetStore();
        }

        if (data.signUpWithEmail.requiresOnboarding) {
          trackDashboardEvent("auth_result", {
            auth_flow: "signup_email",
            result: "success",
            destination: "onboarding",
          });
          navigate("/onboarding");
        } else {
          trackDashboardEvent("auth_result", {
            auth_flow: "signup_email",
            result: "success",
            destination: "dashboard",
          });
          window.location.href = "/";
        }
      } else {
        trackDashboardEvent("auth_result", {
          auth_flow: "signup_email",
          result: "failure",
          error_message: data.signUpWithEmail.error || "signup_failed",
        });
        setError(data.signUpWithEmail.error || "Failed to create account");
      }
    },
    onError: (err) => {
      const isNetworkError =
        !!(err as { networkError?: unknown }).networkError ||
        err.message?.includes('Failed to fetch') ||
        err.message?.includes('NetworkError');
      trackDashboardEvent("auth_result", {
        auth_flow: "signup_email",
        result: "failure",
        error_message: isNetworkError ? "network_error" : err.message || "signup_failed",
      });
      setError(
        isNetworkError
          ? "Something went wrong. Please check your connection and try again."
          : err.message || "Failed to create account"
      );
    },
  });

  // Google auth mutation (for SSO signup with credits)
  const [authenticateWithGoogle, { loading: googleLoading }] = useMutation<{ authenticateWithGoogle: { success: boolean; error?: string | null; tokens?: SignupMutationTokens | null; requiresOnboarding?: boolean | null } }>(AUTHENTICATE_WITH_GOOGLE, {
    onCompleted: async (data) => {
      if (data.authenticateWithGoogle.success) {
        if (data.authenticateWithGoogle.tokens) {
          saveTokens(data.authenticateWithGoogle.tokens.accessToken, data.authenticateWithGoogle.tokens.refreshToken);
          setAuthCookie(data.authenticateWithGoogle.tokens.accessToken);
          await apolloClient.resetStore();
        }

        if (data.authenticateWithGoogle.requiresOnboarding) {
          trackDashboardEvent("auth_result", {
            auth_flow: "signup_google",
            result: "success",
            destination: "onboarding",
          });
          sessionStorage.setItem('pendingGoogleIdToken', sessionStorage.getItem('tempGoogleToken') || '');
          navigate("/onboarding");
        } else {
          trackDashboardEvent("auth_result", {
            auth_flow: "signup_google",
            result: "success",
            destination: "dashboard",
          });
          window.location.href = "/";
        }
      } else {
        trackDashboardEvent("auth_result", {
          auth_flow: "signup_google",
          result: "failure",
          error_message: data.authenticateWithGoogle.error || "signup_failed",
        });
        setError(data.authenticateWithGoogle.error || "Failed to sign up with Google");
      }
    },
    onError: (err) => {
      const isNetworkError =
        !!(err as { networkError?: unknown }).networkError ||
        err.message?.includes('Failed to fetch') ||
        err.message?.includes('NetworkError');
      trackDashboardEvent("auth_result", {
        auth_flow: "signup_google",
        result: "failure",
        error_message: isNetworkError ? "network_error" : err.message || "signup_failed",
      });
      setError(
        isNetworkError
          ? "Something went wrong. Please check your connection and try again."
          : err.message || "Failed to sign up with Google"
      );
    },
  });

  // Handle Google credential response
  const handleGoogleCredential = useCallback(async (response: { credential: string }) => {
    setError("");
    sessionStorage.setItem('tempGoogleToken', response.credential);
    sessionStorage.setItem('pendingGoogleIdToken', response.credential);
    trackDashboardEvent("auth_submit", {
      auth_flow: "signup_google",
    });

    await authenticateWithGoogle({
      variables: {
        input: {
          idToken: response.credential,
          referralCode: referralCodeRef.current || undefined,
        },
      },
    });
  }, [authenticateWithGoogle]);

  // Initialize Google Sign-In
  useEffect(() => {
    const initializeGoogle = () => {
      // Must use typeof check to avoid ReferenceError when google is not defined
      if (typeof google !== 'undefined' && google && googleButtonRef.current && !isGoogleInitialized) {
        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredential,
        });
        
        google.accounts.id.renderButton(googleButtonRef.current, {
          theme: "outline",
          size: "large",
          width: "100%",
          text: "signup_with",
        });
        
        setIsGoogleInitialized(true);
      }
    };

    // Check if script is already loaded
    // Must use typeof check to avoid ReferenceError when google is not defined
    if (typeof google !== 'undefined' && google) {
      initializeGoogle();
    } else {
      // Wait for script to load
      const checkGoogle = setInterval(() => {
        if (typeof google !== 'undefined' && google) {
          initializeGoogle();
          clearInterval(checkGoogle);
        }
      }, 100);

      return () => clearInterval(checkGoogle);
    }
  }, [handleGoogleCredential, isGoogleInitialized, GOOGLE_CLIENT_ID]);

  // Handle OTP input
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otpCode];
    newOtp[index] = value.slice(-1);
    setOtpCode(newOtp);
    
    // Auto-focus next input
    if (value && index < 3) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otpCode[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    const newOtp = [...otpCode];
    for (let i = 0; i < pastedData.length; i++) {
      newOtp[i] = pastedData[i];
    }
    setOtpCode(newOtp);
    if (pastedData.length === 4) {
      otpRefs[3].current?.focus();
    }
  };

  // Handle email submit
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email");
      return;
    }
    
    setError("");
    trackDashboardEvent("auth_submit", {
      auth_flow: "signup_email_verification",
    });
    await sendVerificationCode({
      variables: {
        input: {
          email,
          purpose: "SIGNUP",
        },
      },
    });
  };

  // Handle OTP verify (moves to password step)
  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const code = otpCode.join("");
    if (code.length !== 4) {
      setError("Please enter the complete 4-digit code");
      return;
    }
    setError("");
    setStep("password");
  };

  // Password validation helper
  const isPasswordValid = (pwd: string) => {
    return pwd.length >= 8 && /[A-Z]/.test(pwd) && /[a-z]/.test(pwd) && /[0-9]/.test(pwd);
  };

  // Handle final signup
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isPasswordValid(password)) {
      setError("Password must be at least 8 characters with 1 uppercase, 1 lowercase, and 1 number");
      return;
    }
    
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    setError("");
    trackDashboardEvent("auth_submit", {
      auth_flow: "signup_email",
      has_name: Boolean(name),
    });
    await signUpWithEmail({
      variables: {
        input: {
          email,
          password,
          name: name || undefined,
          verificationCode: otpCode.join(""),
          referralCode: referralCode || undefined,
        },
      },
    });
  };

  const isLoading = sendingCode || signingUp || googleLoading;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        {/* Logo and Header */}
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <img
              src="/Logo White Transparent.png"
              alt="FlowLabs Logo"
              className="h-20 w-auto"
            />
          </div>
          <div className="space-y-2">
            <h1 className="text-4xl font-bold bg-gradient-hero bg-clip-text text-transparent">
              Create Account
            </h1>
            <p className="text-muted-foreground text-lg">
              Join FlowLabs to start streaming
            </p>
          </div>
        </div>

        {/* Signup Card */}
        <Card className="p-8 border-border shadow-xl">
          <div className="space-y-6">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {message && step === "otp" && (
              <Alert className="border-green-500/50 bg-green-500/10">
                <Mail className="h-4 w-4 text-green-500" />
                <AlertDescription className="text-green-500">{message}</AlertDescription>
              </Alert>
            )}

            {/* SSO Credit Banner - only show on email step */}
            {step === "email" && (
              <>
                {/* Google SSO Button */}
                <div className="w-full relative">
                  <div
                    ref={googleButtonRef}
                    className="absolute inset-0 opacity-0 pointer-events-auto z-10"
                    style={{ minHeight: '48px' }}
                  />
                  <Button
                    disabled={isLoading}
                    className="w-full h-12 text-base font-semibold bg-white hover:bg-gray-50 text-gray-900 border border-gray-300 pointer-events-none"
                    variant="outline"
                  >
                    {googleLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Signing up...
                      </>
                    ) : (
                      <>
                        <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                          <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                        </svg>
                        Sign up with Google
                        <span className="ml-2 text-xs text-green-600 font-normal">+100 credits</span>
                      </>
                    )}
                  </Button>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      Or continue with email
                    </span>
                  </div>
                </div>
              </>
            )}

            {/* Step 1: Email */}
            {step === "email" && (
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    className="h-12"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 text-base font-semibold"
                  disabled={isLoading || !email}
                >
                  {sendingCode ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Sending code...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </form>
            )}

            {/* Step 2: OTP */}
            {step === "otp" && (
              <form onSubmit={handleOtpSubmit} className="space-y-4">
                <div className="space-y-4">
                  <Label className="text-center block">
                    Enter the 4-digit code sent to <span className="font-semibold">{email}</span>
                  </Label>
                  <div className="flex justify-center gap-3 pt-2" onPaste={handleOtpPaste}>
                    {otpCode.map((digit, index) => (
                      <Input
                        key={index}
                        ref={otpRefs[index]}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className="w-14 h-14 text-center text-2xl font-bold"
                        autoFocus={index === 0}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 h-12"
                    onClick={() => setStep("email")}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 h-12 text-base font-semibold"
                    disabled={otpCode.join("").length !== 4}
                  >
                    Verify
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    const result = await sendVerificationCode({ variables: { input: { email, purpose: "SIGNUP" } } });
                    if (result.data?.sendVerificationCode?.success) {
                      setMessage("New verification code sent!");
                      setResendCooldown(30);
                    }
                  }}
                  disabled={sendingCode || resendCooldown > 0}
                  className={`w-full text-sm ${resendCooldown > 0 ? 'text-muted-foreground/50 cursor-not-allowed' : 'text-muted-foreground hover:text-primary'}`}
                >
                  {sendingCode ? (
                    "Sending..."
                  ) : resendCooldown > 0 ? (
                    `Resend code in ${resendCooldown}s`
                  ) : (
                    "Didn't receive the code? Resend"
                  )}
                </button>
              </form>
            )}

            {/* Step 3: Password */}
            {step === "password" && (
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Name (optional)
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={isLoading}
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    className="h-12"
                  />
                  {/* Password requirements */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mt-2">
                    <div className={`flex items-center gap-1.5 ${password.length >= 8 ? 'text-green-500' : 'text-muted-foreground'}`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${password.length >= 8 ? 'bg-green-500' : 'bg-muted-foreground/50'}`} />
                      8+ characters
                    </div>
                    <div className={`flex items-center gap-1.5 ${/[A-Z]/.test(password) ? 'text-green-500' : 'text-muted-foreground'}`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${/[A-Z]/.test(password) ? 'bg-green-500' : 'bg-muted-foreground/50'}`} />
                      1 uppercase
                    </div>
                    <div className={`flex items-center gap-1.5 ${/[a-z]/.test(password) ? 'text-green-500' : 'text-muted-foreground'}`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${/[a-z]/.test(password) ? 'bg-green-500' : 'bg-muted-foreground/50'}`} />
                      1 lowercase
                    </div>
                    <div className={`flex items-center gap-1.5 ${/[0-9]/.test(password) ? 'text-green-500' : 'text-muted-foreground'}`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${/[0-9]/.test(password) ? 'bg-green-500' : 'bg-muted-foreground/50'}`} />
                      1 number
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Confirm Password
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                    className="h-12"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 h-12"
                    onClick={() => setStep("otp")}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 h-12 text-base font-semibold"
                    disabled={isLoading || !isPasswordValid(password) || password !== confirmPassword}
                  >
                    {signingUp ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </div>
              </form>
            )}

            <div className="text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="text-primary hover:underline font-medium">
                Sign in
              </Link>
            </div>
          </div>
        </Card>

        {/* Referral Code - outside the card, applies to both signup methods */}
        {step === "email" && (
          <div className="flex items-center gap-3">
            <Label htmlFor="referralCode" className="flex items-center gap-2 text-muted-foreground whitespace-nowrap">
              <Gift className="h-4 w-4" />
              Referral Code
            </Label>
            <Input
              id="referralCode"
              type="text"
              placeholder="FLW123"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              disabled={isLoading}
              className="h-10 uppercase"
            />
          </div>
        )}

        <p className="text-center text-xs text-muted-foreground">
          By signing up, you agree to FlowLabs'{" "}
          <a href="https://flowlabs.live/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Terms of Service</a>
          {" "}and{" "}
          <a href="https://flowlabs.live/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
};

export default Signup;
